import {memo} from 'react';

import styled, {keyframes} from 'styled-components';

import ClearSearch from '~/assets/images/svgIconsAsText/clear_search.svg?react';
import Button from '~/shared/components/Button';
import {getLocalizationService} from '~/shared/services/localisationService';
import {flexCenterVertically} from '~/shared/theme/FlexLayout';
import {flipOnLTR} from '~/shared/theme/flipOnLTR';
import {media} from '~/shared/theme/media';
import {User} from '~/shared/store/models';
import {UserDeviceState} from '~/shared/store/storeModules/userDevice/userDeviceReducer';
import {ApiError} from '~/shared/services/apiErrorService';

import ActionMenuLinks from './ActionMenuLinks';
import {ActionMenuLink} from './types';

const CONDITIONAL_TOP_OFFSET_IN_PX = 5;

const ActionMenuOverlay = styled.div`
  position: fixed;
  background-color: rgba(0, 0, 0, 0.6);
  left: 0;
  right: 0;
  top: 0;
  bottom: -100vh;
  z-index: 1050;
`;

const widthTransition = keyframes`
  from {
    width: 0;
  }

  to {
    width: 300px;
  }
`;

const Root = styled.div<{burgerMenu?: boolean}>`
  ${flipOnLTR`
    left: 0;
  `}
  border-radius: 0;
  ${flexCenterVertically}
  justify-content: flex-start;
  flex-flow: column nowrap;
  background-color: ${({theme}) => theme.colors.surface};
  z-index: ${({theme}) => theme.zIndex.sideMenu};
  animation: ${widthTransition} 0.3s linear 1;
  width: 300px;
  position: fixed;
  height: 100vh;
  box-shadow: ${({theme}) => theme.shadows.shadow2};
  top: 0;
  ${({burgerMenu}) => media[burgerMenu ? 'minLargeTablet' : 'minTablet']`
    padding: 0;
    animation: none;
    position: absolute;
    height: auto;
    top: ${CONDITIONAL_TOP_OFFSET_IN_PX}px;
    border-radius: 12px;
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.10);
  `}
`;

const CloseButtonWrapper = styled.div`
  height: 40px;
  padding: 8px;
  width: 100%;
  ${flexCenterVertically};
  justify-content: flex-start;
`;

const CloseSvg = styled(ClearSearch)`
  width: 24px;
  display: flex;
`;

const CloseButton = styled(Button)`
  display: flex;
`;

const ScrollContainer = styled.div<{topOffset?: number}>`
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  @media only screen and (orientation: landscape) {
    padding-bottom: 30px;
  }

  ${media.minLargeMobile`
    height: 100%;
    ${({topOffset}) => topOffset && `
      max-height: calc(100vh - ${topOffset}px);
    `}
    
    @media only screen and (orientation: landscape) {
      padding-bottom: 0px;
    }
  `}
  ${media.minLargeTablet`
    border-radius: 12px;
    ${({topOffset}) => topOffset && `
      max-height: calc(100vh - ${topOffset}px - ${CONDITIONAL_TOP_OFFSET_IN_PX}px);
    `}
  `}
`;

interface ActionMenuContentProps {
  actionMenuLinks: ActionMenuLink[];
  toggleMenuOpen: (clickEvent?: Event) => void;
  burgerMenu?: boolean;
  topOffset?: number;
  userData?: User;
  userDevice: UserDeviceState;
  moneyCardsError: ApiError | null;
}

const CompanyUserActionMenuContent = ({actionMenuLinks, toggleMenuOpen, burgerMenu, topOffset, userData, userDevice}: ActionMenuContentProps) => {
  const {t} = getLocalizationService();
  const {isMinTablet, isMaxLargeTablet, isMaxLargeMobile} = userDevice;

  const showCloseButton = burgerMenu ? isMaxLargeTablet : isMaxLargeMobile;
  const showOverlay = burgerMenu ? isMaxLargeTablet : !isMinTablet;

  const handleOnClick = (link: ActionMenuLink) => {
    link.onClick?.();
  };

  return (
    <>
      {showOverlay && <ActionMenuOverlay />}
      <div>
        <Root burgerMenu={burgerMenu}>
          {showCloseButton && (
            <CloseButtonWrapper>
              <CloseButton onClick={toggleMenuOpen}>
                <CloseSvg alt={t('close')} />
              </CloseButton>
            </CloseButtonWrapper>
          )}
          <ScrollContainer topOffset={topOffset}>
            <ActionMenuLinks
              links={actionMenuLinks}
              onClick={handleOnClick}
              userData={userData}
              closeMenu={toggleMenuOpen}
            />
          </ScrollContainer>
        </Root>
      </div>
    </>
  );
};

export default memo(CompanyUserActionMenuContent);
